import useSystemStore from 'parts/systemStore/systemStore'
import { ComponentType, lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { Routes } from 'react-router-dom'
import { useSetUser } from './fn/setUser'
import { useGetUserStatusInStore, useSetSchool } from './fn/getData'
import getAuthRouter from '../routes/special/authRouter'
import { useGetUserRole, useGoToFirstSchool } from 'parts/utils/hooks/hooks'
import autoLogin from 'parts/services/AutoLogin'
import UrlService from 'parts/services/UrlService'
import ScrollManagerElement from './fn/ScrollManagerElement'
import { useGetScrollDataFromLocalStorageAndSaveToStore } from './scrollManager/getScrollDataFromLocalStorage'
import loadRestTranslations from 'parts/i18n/loadRestTranslations'
import { recoverFromChunkLoadError } from 'parts/utils/chunkLoadError'
import Loader from 'ui/Loader/Loader'
import 'antd/dist/reset.css'
import './css/general.scss'
import './css/fonts.scss'
import './css/variables.scss'

// Роутер роли грузится вместе с основной частью словаря переводов: в главном
// бандле лежат только разделы, нужные странице входа.
function lazyRouter<Props>(
	factory: () => Promise<{ default: ComponentType<Props> }>
) {
	return lazy(() =>
		recoverFromChunkLoadError(() =>
			Promise.all([factory(), loadRestTranslations()]).then(
				([module]) => module
			)
		)
	)
}

const AdminRouter = lazyRouter(() => import('../routes/adminRouter'))
const StudentRouter = lazyRouter(() => import('../routes/studentRouter'))
const CuratorRouter = lazyRouter(() => import('../routes/curatorRouter'))
const ManagerRouter = lazyRouter(() => import('../routes/managerRouter'))

function App() {
	// Если в URL есть сгенерированный токен для авторизации, то поставить в LocalStorage,
	// чтобы получить на странице входа. Это должно работать на домене школы.
	autoLogin.useMoveGeneratedTokenFromUrlToLocalStorage()

	useSetUser()
	useGetScrollDataFromLocalStorageAndSaveToStore()

	// Отправить событие в Google Analytics
	window.dataLayer.push({
		event: 'pageview',
	})

	return (
		<div className="app">
			{UrlService.isRootDomain() ? <RootDomain /> : <SchoolDomain />}
		</div>
	)
}

// export default Sentry.withProfiler(App)
export default App

// ROOT DOMAIN ------------------------------------------------------------------------

function RootDomain() {
	const userStatusInStore = useGetUserStatusInStore()

	if (userStatusInStore == 'loading') {
		return <Spin />
	} else if (userStatusInStore === 'notExists') {
		return <RootDomainNotAuthorizedUser />
	} else {
		return <RootDomainAuthorizedUser />
	}
}

function RootDomainNotAuthorizedUser() {
	return <Routes>{getAuthRouter()}</Routes>
}

function RootDomainAuthorizedUser() {
	const userRoles = useGetUserRole()
	useGoToFirstSchool(userRoles)

	// Переадресовать на домен школы с основного домена
	// SchoolService.goToFirstSchoolUrl(userRoles)

	return <Spin />
}

// SCHOOL DOMAIN ----------------------------------------------------------------------

function SchoolDomain() {
	const userStatusInStore = useGetUserStatusInStore()

	if (userStatusInStore == 'loading') {
		return <Spin />
	} else if (userStatusInStore === 'notExists') {
		return <SchoolDomainNotAuthorizedUser />
	} else {
		return <SchoolDomainAuthorizedUser />
	}
}

function SchoolDomainNotAuthorizedUser() {
	return <Routes>{getAuthRouter()}</Routes>
}

function SchoolDomainAuthorizedUser() {
	const userRoles = useGetUserRole()
	const user = useSystemStore((store) => store.user)

	useSetSchool()
	const school = useSystemStore((store) => store.school)

	if (!userRoles.rolesGot || !school) {
		return <Spin />
	}

	return (
		<Suspense fallback={<Loader />}>
			<ScrollManagerElement />
			{userRoles.isAdmin && <AdminRouter user={user} />}
			{userRoles.isManager && <ManagerRouter user={user} />}
			{userRoles.isCurator && <CuratorRouter user={user} />}
			{userRoles.isStudent && <StudentRouter user={user} />}
		</Suspense>
	)
}
